import Component from "../../lib/component";
import {preloadImage} from "../../helpers/img";

/**
 * ProgressiveImage — shows a low-res placeholder immediately, then cross-fades a full-res image
 * over it once that image has actually decoded.
 *
 * The placeholder is a separate `img` element that this component NEVER writes to, and the
 * full-res `img` starts with no `src` attribute at all. Nothing that can fail is ever put in front
 * of the user: a URL is only assigned once `preloadImage` has confirmed it loads. So the visible
 * result is always either the placeholder or the full image — never a broken-image glyph.
 *
 * That matters most in the native apps, where the placeholder is inlined as a base64 data URI (it
 * needs no network at all) but the full-res image is not bundled in the seed. Offline, the upgrade
 * simply never happens and the placeholder stays. See
 * plans/2026-07-22--submarker-progressive-image-never-broken.md.
 */
export const ProgressiveImageConfig = {
    componentTypeKey: 'ProgressiveImage',
    baseHtmlClass: 'progressive_image',
    selector: '.progressive_image',

    fullSelector: '.progressive_image__full',
    fullUrlDatasetKey: 'progressive_image__full_url',
    // Development only — holds the placeholder on screen for a beat so the upgrade is actually
    // watchable. Emitted by render_progressive_image in the development environment only, so there
    // is nothing to strip before shipping.
    delayMsDatasetKey: 'progressive_image__delay_ms',

    loadedClassModifier: 'loaded',
};

export class ProgressiveImage extends Component {

    static get config() {
        return ProgressiveImageConfig;
    }

    constructor(element) {
        super({element: element, config: ProgressiveImage.config});

        this.fullImgElement = this.element.querySelector(ProgressiveImage.config.fullSelector);
        this.fullUrl = this.fullImgElement ? this.fullImgElement.dataset[ProgressiveImage.config.fullUrlDatasetKey] : null;
        this.delayMs = this.fullImgElement ? parseInt(this.fullImgElement.dataset[ProgressiveImage.config.delayMsDatasetKey], 10) || 0 : 0;

        this.isLoaded = false;
        this.delayTimeoutId = null;

        // A timer outlives the element, so it has to be torn down with the component.
        this.onDestroy(() => { if (this.delayTimeoutId) clearTimeout(this.delayTimeoutId); });

        this.load();
    }

    load() {
        if (!this.fullImgElement || !this.fullUrl) return;

        preloadImage(this.fullUrl)
            .then(this.handleImagePreloaded.bind(this))
            .catch(this.handleImageFailed.bind(this));
    }

    handleImagePreloaded() {
        // Delay the SWAP rather than the fetch, so the placeholder is guaranteed to be the thing on
        // screen for the whole delay and the cross-fade is what you see at the end of it.
        if (!this.delayMs) return this.handleImageLoaded();

        this.delayTimeoutId = setTimeout(this.handleImageLoaded.bind(this), this.delayMs);
    }

    handleImageLoaded() {
        // The component can be reaped while the image is in flight (the submarker overlay is
        // opened and closed freely), so the element may no longer be ours to touch.
        if (this.isDestroyed) return;

        // Assigning a pre-verified URL cannot fail visibly, and the browser serves it from cache.
        this.fullImgElement.src = this.fullUrl;
        delete this.fullImgElement.dataset[ProgressiveImage.config.fullUrlDatasetKey];

        this.isLoaded = true;
        this.render();
    }

    handleImageFailed() {
        // Deliberately nothing to undo: the placeholder was never touched, so it is still on
        // screen. The upgrade just doesn't happen.
    }

    render() {
        this.conditionallySetHtmlClassState(this.isLoaded, ProgressiveImage.config.loadedClassModifier);
    }

}
